import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy, EventEmitter, Output } from '@angular/core';
import { Korebog, UgerapportSager, UgerapportKunder, IndexCard } from '@store/models';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormArray, UntypedFormControl } from '@angular/forms';
import { AlertService } from 'app/core/alerts/alert.service';
import pdfMake from 'pdfmake/build/pdfmake';
pdfMake.fonts = {
    DDIN: {
        normal: `${window.location.origin}/assets/D-DIN.ttf`,
        bold: `${window.location.origin}/assets/D-DIN-Bold.ttf`,
        italics: `${window.location.origin}/assets/D-DIN-Italic.ttf`,
    }
};
import { ConfirmService } from 'app/shared/confirm-dialog/confirm-dialog.service';
import { LuxonUtils } from '@shared';

@Component({
    selector: 'app-mileage-log-ui',
    templateUrl: './mileage-log.component.html',
    styleUrls: ['../timetrack.shared.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MileageLogComponent implements OnChanges {

    form: UntypedFormGroup;

    @Input() korebog: Korebog[];
    @Input() ugerapportSager: UgerapportSager[];
    @Input() ugerapportKunder: UgerapportKunder[];
    @Input() user: IndexCard;
    @Output() save = new EventEmitter<Korebog>();
    @Output() delete = new EventEmitter<number>();

    editId: number[] = [0];
    displayColumns = ['dato', 'kunde', 'sag', 'destination', 'beskrivelse', 'km', 'slet'];

    get totalKm() {
        return this.korebog?.filter(kb => !kb.afregnet).reduce((acc, curr) => acc + (curr.km || 0), 0);
    }

    get totalKmAfregnet() {
        return this.korebog?.filter(kb => !!kb.afregnet).reduce((acc, curr) => acc + (curr.km || 0), 0);
    }

    get formKorebog() {
        return (this.form.get('korebog') as UntypedFormArray).controls;
    }

    constructor(
        private fb: UntypedFormBuilder,
        private alert: AlertService,
        private confirm: ConfirmService
    ) { }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.korebog) {
            this.createForm();
            this.korebog.map(kb => this.formKorebog.push(this.createEntry(kb)));
            this.formKorebog.push(this.createEntry({
                ...new Korebog(),
                dato: LuxonUtils.utc().startOf('day').toJSDate()
            }));
            this.editId = [0];
        }
    }

    createForm() {
        this.form = this.fb.group({
            korebog: this.fb.array([])
        });
    }

    createEntry(kb: Korebog) {
        return this.fb.group({
            id: kb.id,
            mid: kb.mid,
            kid: kb.kid,
            label: kb.label,
            dato: kb.dato,
            beskrivelse: kb.beskrivelse,
            km: kb.km,
            kontor: kb.kontor,
            sag: kb.sag,
            sagBeskrivelse: kb.sagBeskrivelse,
            destination: kb.destination,
            slettet: kb.slettet,
            afregnet: kb.afregnet
        });
    }

    trackById(_: number, kb: Korebog) {
        return kb.id;
    }

    isEdit(id: number): boolean {
        return this.editId.indexOf(id) > -1;
    }

    onEdit(korebog: Korebog) {
        if (korebog.afregnet !== null) {
            this.alert.createAlert('Afregnede poster kan ikke redigeres', 'info');
        } else if (this.editId.indexOf(korebog.id) < 0) {
            this.editId.push(korebog.id);
        }
    }

    onDelete(event: MouseEvent, korebog: Korebog) {
        event.stopPropagation();
        this.confirm.question(
            `Er du sikker på du vil slette posten "${korebog.beskrivelse}" med ${korebog.km} km?`,
            'Ja',
            'Nej'
        ).then(yes => {
            if (yes) { this.delete.emit(korebog.id); }
        });
    }

    onSave(event: MouseEvent, kb: UntypedFormControl) {
        event.stopPropagation();
        const saveKb: Korebog = kb.value;
        if (saveKb.id === 0) {
            kb.reset({id: 0, dato: new Date()});
            saveKb.mid = this.user.cardId;
            saveKb.kontor = this.user.kontor;
        } else {
            const orgKb = this.korebog.find(k => k.id === kb.value.id);
            kb.patchValue(orgKb);
            this.editId = this.editId.filter(e => e !== kb.value.id);
        }
        this.save.emit(saveKb);
    }

    onCancel(event: MouseEvent, kb: UntypedFormControl) {
        event.stopPropagation();
        if (kb.value.id === 0) {
            kb.reset({id: 0, dato: new Date()});
        } else {
            const orgKb = this.korebog.find(k => k.id === kb.value.id);
            kb.patchValue(orgKb);
            this.editId = this.editId.filter(e => e !== kb.value.id);
        }
    }

    pdfMileage() {
        const kbEntries = this.korebog.filter(k => k.afregnet === null).map(kb => ([
            {style: 'normal', text: LuxonUtils.fromIsoUtc(kb.dato).toFormat('dd-LL-yyyy')},
            {style: 'normal', text: (kb.label?.toString() || '') + '\n' + (kb.sagBeskrivelse?.toString() || '')},
            {style: 'normal', text: (kb.destination?.toString() || '') + '\n' + (kb.beskrivelse?.toString() || '') },
            {style: 'normal', alignment: 'right', text: kb.km?.toString() || ''}
        ]));
        const documentDefinition = {
            content: [
                {
                    table: {
                        widths: ['*', '*'],
                        body: [
                            [
                                {text: `Kørebog for ${this.user.fornavn} ${this.user.efternavn}`, style: 'header'},
                                {text: `Reg. nr. ${this.user.bilregnr}`, style: 'header', alignment: 'right'}
                            ]
                        ]
                    },
                    layout: {
                        defaultBorder: false
                    }
                },
                {
                    table: {
                        headerRows: 1,
                        widths: ['auto', 'auto', '*', 'auto'],
                        body: [
                            [
                                {style: 'normal', fillColor: '#cccccc', text: 'Dato'},
                                {style: 'normal', fillColor: '#cccccc', text: 'Kunde / sag'},
                                {style: 'normal', fillColor: '#cccccc', text: 'Destination / Beskrivelse'},
                                {style: 'normal', fillColor: '#cccccc', text: 'KM'}
                            ],
                            ...kbEntries,
                            [
                                {style: 'normal', border: [false, true, false, true], text: 'I alt'},
                                {border: [false, true, false, true], text: ''},
                                {border: [false, true, false, true], text: ''},
                                {style: 'normal', border: [false, true, false, true], text: this.totalKm?.toString()}
                            ]
                        ]
                    },
                    layout: {
                        defaultBorder: false
                    }
                }
            ],
            styles: {
                normal: {fontSize: 10},
                header: {fontSize: 18}
            },
            defaultStyle: {
                font: 'DDIN'
            }
        };
        pdfMake.createPdf(documentDefinition).open();
    }

    onClientChange(tr: UntypedFormControl) {
        tr.patchValue({sag: undefined});
    }

}
